import type { ChangeEvent, FC, KeyboardEvent } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import colors from 'app/styles/common/colors.module.scss';
import cn from 'classnames';

import { useContacts } from 'hooks/useContacts';
import { useIsMobile } from 'hooks/useMedia';
import { getTestIdByParts } from 'utils/getTestIdByParts';
import type { WithTestId } from 'types/app';

import { AnalyticsTypes } from '@Modules/analytics/types';
import { analytics } from '@Modules/analytics/analytics';

import { Button } from '@Atomic/atoms/Button';
import { Icons } from '@Atomic/atoms/Icons';
import { Input } from '@Atomic/atoms/Input';
import { Tags } from '@Atomic/atoms/Tags';
import type { TagOption } from '@Atomic/atoms/Tags/types';

import styles from './offerBid.module.scss';
import type { AskDiscount, AskDiscountQuestion } from './types';
import { QuestionActionType } from './types';
import { sanitizePriceInput } from './utils';

type OfferBidProps = WithTestId<{
  offerId: number;
  askDiscount: AskDiscount;
  className?: string;
}>;

// Fits a spaced 12-digit amount ("999 999 999 999") with headroom for a
// currency suffix; only bounds further typing, never truncates a value.
const PRICE_INPUT_MAX_LENGTH = 20;

export const OfferBid: FC<OfferBidProps> = ({ offerId, askDiscount, className, testId }) => {
  const { t } = useTranslation();
  const { openChat } = useContacts(offerId);
  const isMobile = useIsMobile();

  const { questions, startPlaceholder } = askDiscount;
  const initialQuestion: AskDiscountQuestion | undefined =
    questions.find((question) => question.selected) ?? questions[0];

  const [selectedQuestion, setSelectedQuestion] = useState(initialQuestion);
  // The input SHOWS the compact amount ("110 810 сум") — the question's
  // `placeholder` — not the backend's full greeting `value`. The full message
  // is resolved at send time (see resolveOutgoingMessage). An initially
  // selected (or sole) OPEN_CHAT question starts in custom-amount mode with an
  // empty input, same as handleTagChange — its `placeholder` is never
  // displayed and must never leak into an outgoing message.
  const [message, setMessage] = useState(
    initialQuestion?.actionType === QuestionActionType.OPEN_CHAT
      ? ''
      : (initialQuestion?.placeholder ?? '')
  );

  // Defensive: the sole caller already gates on a non-empty `questions` array,
  // but never assume it — an empty array would otherwise crash on `.placeholder`.
  if (!selectedQuestion) {
    return null;
  }

  const options: TagOption[] = questions.map((question, index) => ({
    label: question.title,
    value: String(question.id),
    testId: getTestIdByParts(testId, { value: 'tag', index }),
  }));

  const handleTagChange = (value: string | string[]): void => {
    if (Array.isArray(value)) {
      return;
    }

    const question = questions.find((item) => String(item.id) === value);

    if (!question) {
      return;
    }

    if (question.actionType === QuestionActionType.OPEN_CHAT) {
      // Two-tier analytics (BP-7402): the cross-platform click event fires on
      // the raw tap — before the auth gate, chat navigation (mobile) or local
      // mode switch (desktop) — matching the APP behavior; the web-only
      // `offer_bid_*` events below keep their "chat actually opened" /
      // "mode switched" semantics.
      analytics.offerClickCustomQuestion({
        offer_id: offerId,
        placement: AnalyticsTypes.OfferQuestionPlacement.OFFER_BID,
      });

      // Mobile keeps APP parity: the tap navigates to the full chat page with
      // the polite greeting drafted (not sent); the block state must not
      // move. The chip never becomes selected, so every repeat tap is a fresh
      // Radix value change and re-opens the chat. `offerBidOpenChat` fires
      // only once the chat actually opens (`onOpen`), so an auth-gated open
      // is not over-counted.
      if (isMobile) {
        openChat({
          message: question.value,
          mode: 'draft',
          onOpen: () => analytics.offerBidOpenChat({ offer_id: offerId, question_id: question.id }),
        });

        return;
      }

      // Desktop: purely local mode switch — no chat opens and nothing is
      // drafted. The input clears to the custom-amount prompt; the chat opens
      // only on Send. Tracked as its own event (not `offerBidOpenChat`): no
      // chat opens here and the user may be anonymous.
      setSelectedQuestion(question);
      analytics.offerBidCustomAmountSelect({ offer_id: offerId, question_id: question.id });
      setMessage('');

      return;
    }

    setSelectedQuestion(question);
    setMessage(question.placeholder);
  };

  const handleMessageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    // Desktop forbids letters in the price input (typed or pasted); mobile
    // keeps free text for APP parity.
    setMessage(isMobile ? value : sanitizePriceInput(value));
  };

  // The block input displays the compact amount, but the chat must receive the
  // full polite message:
  // - untouched PREFILL input → the selected question's backend-built `value`
  // - custom amount → the OPEN_CHAT stem + amount + "?", or the raw text when
  //   the payload has no OPEN_CHAT question to take the stem from
  const resolveOutgoingMessage = (): string => {
    const trimmed = message.trim();

    // The OPEN_CHAT question's backend `placeholder` is never displayed (the
    // input shows the custom-amount prompt instead), so the untouched-input
    // shortcut applies only to PREFILL questions.
    if (
      selectedQuestion.actionType !== QuestionActionType.OPEN_CHAT &&
      trimmed === selectedQuestion.placeholder.trim()
    ) {
      return selectedQuestion.value;
    }

    const stem = questions.find(
      (question) => question.actionType === QuestionActionType.OPEN_CHAT
    )?.value;

    if (!stem) {
      return trimmed;
    }

    // The backend-built PREFILL messages end with a single question mark
    // ("…Продадите за 98 000 сум?") — the composed message must match.
    // Trailing punctuation is trimmed from the amount first so "500!"
    // composes to "…за 500?" rather than "…за 500!?".
    const amount = trimmed.replace(/[?!.,;:\s]+$/, '');

    return `${stem} ${amount}?`;
  };

  const handleSend = () => {
    // An empty input is a designed state (OPEN_CHAT selection clears it) —
    // don't open a chat that would send nothing or fire send analytics.
    if (!message.trim()) {
      return;
    }

    // Two-tier analytics (BP-7402): the cross-platform click event fires on
    // the raw press (APP parity, even for auth-gated anonymous users), while
    // the web-only `offerBidSend` fires from `onOpen`, i.e. only when the
    // send is actually executed (immediately when logged in, or after login).
    analytics.offerClickSendQuestion({
      offer_id: offerId,
      question_id: selectedQuestion.id,
      placement: AnalyticsTypes.OfferQuestionPlacement.OFFER_BID,
    });

    openChat({
      message: resolveOutgoingMessage(),
      mode: 'send',
      onOpen: () => analytics.offerBidSend({ offer_id: offerId, question_id: selectedQuestion.id }),
    });
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSend();
    }
  };

  const isCustomAmountMode = selectedQuestion.actionType === QuestionActionType.OPEN_CHAT;

  return (
    <div className={cn(styles.container, className)} data-testid={testId}>
      <div className={styles.inputContainer}>
        <Input
          value={message}
          onChange={handleMessageChange}
          onKeyDown={handleKeyDown}
          placeholder={
            isCustomAmountMode
              ? t('offer.offerBid.customAmountPlaceholder')
              : selectedQuestion.placeholder || startPlaceholder || undefined
          }
          className={styles.input}
          aria-label={t('offer.offerBid.inputLabel')}
          // The desktop custom-amount mode is effectively a numeric field
          // (letters are stripped) — hint the numeric keyboard on
          // desktop-with-touch devices. Mobile keeps free text (APP parity).
          inputMode={isCustomAmountMode && !isMobile ? 'numeric' : undefined}
          maxLength={isMobile ? undefined : PRICE_INPUT_MAX_LENGTH}
          testId={getTestIdByParts(testId, 'input')}
        />

        <Button
          intent="accent"
          presentation="filled"
          size="small"
          circle
          iconLeft={<Icons.SendFill width={20} height={20} color={colors.white} />}
          onClick={handleSend}
          testId={getTestIdByParts(testId, 'send')}
          aria-label={t('offer.offerBid.sendLabel')}
        />
      </div>

      <Tags
        options={options}
        color="grey"
        size="medium"
        className={styles.tags}
        multiple={false}
        allowEmptySelection={false}
        value={String(selectedQuestion.id)}
        onChange={handleTagChange}
        aria-label={t('offer.offerBid.optionsLabel')}
        testId={getTestIdByParts(testId, 'tags')}
      />
    </div>
  );
};
